import * as React from "react";
import {observer} from "mobx-react";
import {Box, Fab, IconButton, Link, Paper, TextField, Typography, useTheme} from "@mui/material";
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReactMarkdown from "react-markdown";
import useFeature from "../../useFeature";
import ChatWidgetFeature from "./ChatWidgetFeature";
import {styles} from "./ChatWidgetStyles";
import {useEffect, useRef} from "react";
import {useApplication} from "../../ApplicationProvider";

function ChatWidgetScreen() {
    const theme = useTheme();
    const style = styles(theme);
    const application = useApplication();
    const feature = useFeature(() => new ChatWidgetFeature(application), [application]);
    const inputRef = useRef(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (feature?.isOpen) {
            inputRef.current?.focus();
        }
    }, [feature?.isOpen]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [feature?.messages?.length, feature?.isTyping]);

    useEffect(() => {
        if (feature?.isOpen) {
            messagesEndRef.current?.scrollIntoView({behavior: 'instant'});
        }
    }, [feature?.isOpen]);

    if (!feature || !feature.loaded) {
        return null;
    }

    const isSendDisabled = feature.isTyping;
    const isInputDisabled = isSendDisabled;
    const inputPlaceholder = "Escribí tu mensaje...";

    const sendMessage = () => {
        feature.sendMessage();
    }

    return (
        <Box sx={style.floatingContainer}>
            {feature.isOpen && (
                <Paper elevation={8} sx={style.chatPanel}>
                    <Box sx={style.chatHeader}>
                        <Box sx={style.headerIconSlot}>
                            <SupportAgentIcon/>
                        </Box>
                        <Box sx={style.headerCenter}>
                            <Typography sx={style.headerTitle}>Asistente Tienda</Typography>
                            <Box sx={style.headerStatus}>
                                <Box sx={style.statusDot}/>
                                <Typography sx={style.headerSubtitle}>En línea</Typography>
                            </Box>
                        </Box>
                        <Box sx={style.headerCloseSlot}>
                            <IconButton aria-label="Cerrar chat" onClick={() => feature.close()}>
                                <CloseIcon sx={style.closeButton}
                                />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={style.messagesContainer}>
                        {feature.messages.map((message) => (
                            <ChatMessageBubble key={message.id} message={message} style={style}/>
                        ))}
                        {feature.isTyping && (
                            <Box sx={style.messageRow}>
                                <Box sx={[style.messageBubble, style.typingBubble]}>
                                    <Typography variant="body2" color="inherit">Escribiendo...</Typography>
                                </Box>
                            </Box>
                        )}
                        <Box ref={messagesEndRef}/>
                    </Box>
                    <Box sx={style.inputContainer}>
                        <TextField
                            inputRef={inputRef}
                            placeholder={inputPlaceholder}
                            sx={style.inputField}
                            value={feature.draft}
                            onChange={(event) => feature.updateDraft(event.target.value)}
                            disabled={isInputDisabled}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    if (!isSendDisabled) {
                                        sendMessage();
                                    }
                                }
                            }}
                        />
                        <IconButton
                            aria-label="Enviar mensaje"
                            onClick={sendMessage}
                            disabled={isSendDisabled}
                            sx={[style.sendButton, isSendDisabled && style.sendButtonDisabled]}
                        >
                            <SendIcon/>
                        </IconButton>
                    </Box>
                    <Typography sx={style.disclaimer}>
                        Tus consultas se procesan con inteligencia artificial y se registran para mejorar el servicio. Evitá compartir datos sensibles.
                    </Typography>
                </Paper>
            )}
            <Fab
                aria-label={feature.isOpen ? "Cerrar chat" : "Abrir chat"}
                onClick={() => feature.toggleOpen()}
                sx={[style.fab, feature.isOpen && style.fabOpen]}
            >
                {feature.isOpen ? <CloseIcon/> : <ChatBubbleOutlineIcon/>}
            </Fab>
        </Box>
    );
}

const markdownComponents = {
    p: ({children}) => (
        <Typography variant="body2" color="inherit" sx={{m: 0}}>{children}</Typography>
    ),
    strong: ({children}) => (
        <Typography component="span" variant="body2" color="inherit" sx={{fontWeight: 'bold'}}>{children}</Typography>
    ),
    em: ({children}) => (
        <Typography component="span" variant="body2" color="inherit" sx={{fontStyle: 'italic'}}>{children}</Typography>
    ),
    ul: ({children}) => (
        <Box component="ul" sx={{m: 0, pl: 2}}>{children}</Box>
    ),
    ol: ({children}) => (
        <Box component="ol" sx={{m: 0, pl: 2}}>{children}</Box>
    ),
    li: ({children}) => (
        <Typography component="li" variant="body2" color="inherit">{children}</Typography>
    ),
    a: ({href, children}) => (
        <Link href={href} target="_blank" rel="noopener noreferrer" color="inherit" underline="always">{children}</Link>
    ),
};

function ChatMessageBubble({message, style}) {
    const isUser = message.author === "user";
    return (
        <Box sx={[style.messageRow, isUser && style.messageRowUser]}>
            <Box sx={[style.messageBubble, isUser && style.messageBubbleUser]}>
                {isUser ? (
                    <Typography variant="body2" color="inherit">{message.content}</Typography>
                ) : (
                    <ReactMarkdown components={markdownComponents}>{message.content}</ReactMarkdown>
                )}
            </Box>
            <Typography sx={style.messageMeta}>{isUser ? "Vos" : "Asistente"}</Typography>
        </Box>
    );
}

export default observer(ChatWidgetScreen);
